import { CONFIG_ELEMENTS } from '@vedalib/editor/lib/elements'
import { useCallback, useMemo } from 'react'

import { listToTree } from 'components/sections/ProjectStructure/utils'
import { SectionTypeEnum } from 'services/Store/Project/enums'
import { useProjectContext } from 'services/Store/Project/hooks'
import {
  getProgress,
  getSection,
  getSections,
  isSectionDisabled,
} from 'services/Store/Project/selectors'
import { useNavigationAction } from 'services/Store/Project/triggers'
import store from 'services/Store/store'
import { useGetEntityAnalyticsConfig } from 'services/lms/hooks/useGetEntityAnalyticsConfig'

import CourseStructureTree from './CourseStructureTree/CourseStructureTree'
import type { CourseStructureElementType } from './courseStructureTypes'

const LIST_SECTIONS = [SectionTypeEnum.chapter, SectionTypeEnum.landing, SectionTypeEnum.test]

const CourseStructureElement: CourseStructureElementType = ({ styles, element, font, block }) => {
  const sections = useProjectContext(getSections, LIST_SECTIONS) || element.value.sectionsDefault
  const onNavigate = useNavigationAction()
  const section = useProjectContext(getSection)
  const { entityAnalyticsConfig, sendXapiData, baseObjectExtensions } = useGetEntityAnalyticsConfig(
    {
      blockId: block?.uuid || '',
      elementType: CONFIG_ELEMENTS.courseStructure.type,
      elementId: element.id,
    },
  )

  const state = store.getState().project
  const treeSections = useMemo(() => listToTree(sections), [sections])

  const onSectionPick = useCallback(
    (sectionId: string) => {
      onNavigate({ sectionId, currentSection: section })
      const sectionIndex = sections.findIndex((s) => s.id === sectionId)
      const sectionName = sections[sectionIndex]?.name || ''
      const data = entityAnalyticsConfig.interacted({
        resultExtensions: {
          toSectionId: sectionId,
          toSectionIndex: sectionIndex,
          toSectionName: sectionName,
        },
        objectExtensions: baseObjectExtensions,
        elementId: block?.uuid || '',
      })
      sendXapiData(data)
    },
    [
      entityAnalyticsConfig,
      block?.uuid,
      baseObjectExtensions,
      onNavigate,
      section,
      sections,
      sendXapiData,
    ],
  )
  const getSectionProgress = useCallback((id: string) => getProgress(state, id), [state])
  const getDisabledSection = useCallback((id: string) => isSectionDisabled(state, id), [state])

  return (
    <CourseStructureTree
      font={font}
      getDisabledSection={getDisabledSection}
      getSectionProgress={getSectionProgress}
      nodes={treeSections}
      onSectionPick={onSectionPick}
      section={section}
      sections={sections}
      styles={styles}
      value={element.value}
    />
  )
}

export default CourseStructureElement
